/**
 * The KHR_interactivity `types[]` table as the exporter builds it, and the
 * inverse used on import.
 *
 * Ratified rules (spec § Types): `signature` is one of the spec's value types
 * or `"custom"`; custom semantics come from another extension; a built-in
 * signature appears at most once. Our GraphData types carry two Trace
 * signatures (`string`, `color` — docs-internal/TRACE_interactivity_types.md)
 * plus the exporter's `any` for polymorphic sockets it cannot narrow. All three
 * go on the wire as
 *
 *     { "signature": "custom", "extensions": { "TRACE_interactivity": { "type": "string" } } }
 *
 * The exporter interns every type it references (variables, events, inline
 * literals, socket declarations, `pointer/*` `type` configs), so the emitted
 * table holds exactly what the graph uses, each built-in once.
 */

import type { TypeSignature } from '../../scenedata/behaviors/behaviorgraphdata';
import type { KHRInteractivityType } from '../../utils/ModelLoader';
import { TRACE_INTERACTIVITY_EXTENSION } from './traceOpContracts';

/** Signatures the ratified spec defines (everything else is `custom`). */
const BUILT_IN = new Set<string>([
    'bool', 'int', 'float', 'float2', 'float3', 'float4',
    'float2x2', 'float3x3', 'float4x4', 'ref',
]);

/** Our type vocabulary on the export side: GraphData signatures, `ref`, and `any`. */
export type ExportTypeName = TypeSignature | 'ref' | 'any';

export function isBuiltInSignature(signature: string): boolean {
    return BUILT_IN.has(signature);
}

export class KHRTypeTable {
    private readonly _entries: KHRInteractivityType[] = [];
    private readonly _index = new Map<string, number>();

    /** Index of `name` in the emitted table, adding it on first use. */
    index(name: string): number {
        const existing = this._index.get(name);
        if (existing !== undefined) return existing;
        const entry: KHRInteractivityType = isBuiltInSignature(name)
            ? { signature: name }
            : { signature: 'custom', extensions: { [TRACE_INTERACTIVITY_EXTENSION]: { type: name } } };
        const index = this._entries.length;
        this._entries.push(entry);
        this._index.set(name, index);
        return index;
    }

    get entries(): KHRInteractivityType[] {
        return this._entries;
    }
}

/**
 * Import side: the GraphData signature for a KHR type entry. Trace custom
 * types map back to their Trace signature; `any` (only ever referenced by
 * socket declarations and defaulted literals, both dropped on import) and
 * any foreign custom type become `string`, the opaque pass-through type.
 */
export function signatureFromKHRType(type: KHRInteractivityType): TypeSignature {
    if (type.signature !== 'custom') return type.signature as TypeSignature;
    const traceType = type.extensions?.[TRACE_INTERACTIVITY_EXTENSION]?.type;
    if (traceType === 'color') return 'color';
    return 'string';
}
